import { unitDef, unitBuildHotkey, type PlayerId, type UnitKind } from "@dailywars/core/engine";
import { Info } from "lucide-react";

import { Button } from "#/components/ui/button";
import { Infotip, InfotipContent, InfotipTrigger } from "#/components/ui/infotip";
import { getSpriteUnitIconImage } from "#/game/unitSprites";

import { CoinMoneyIcon } from "./CoinMoneyIcon";
import {
  BUILD_OPTION_DISABLED_REASON_LABEL,
  BuildListScrollContainer,
} from "./ProductionBuildMenuShared";
import type { BuildOption } from "./productionBuildMenuTypes";

function BuildOptionInfoButton({
  unitName,
  onInspect,
}: {
  unitName: string;
  onInspect: () => void;
}) {
  return (
    <Button
      type="button"
      size="icon-sm"
      variant="outline"
      onClick={onInspect}
      aria-label={`View ${unitName} info`}
    >
      <Info aria-hidden />
    </Button>
  );
}

function BuildOptionBuyAction({
  option,
  unitName,
  onChoose,
  hotkey,
  imgSrc,
}: {
  option: BuildOption;
  unitName: string;
  onChoose: (kind: UnitKind) => void;
  hotkey: string | null;
  imgSrc: string;
}) {
  const buyButton = (
    <button
      type="button"
      disabled={option.disabled}
      onClick={() => onChoose(option.kind)}
      aria-label={`Buy ${unitName}`}
      className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-45"
    >
      {hotkey !== null && (
        <span
          aria-hidden
          className="hidden shrink-0 text-sm tabular-nums text-muted-foreground sm:inline"
        >
          {hotkey}
        </span>
      )}
      <img src={imgSrc} alt={unitName} className="size-7 shrink-0 rounded-sm" />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{unitName}</span>
      <span className="flex shrink-0 items-center gap-1 text-sm tabular-nums text-muted-foreground">
        {option.cost}
        <CoinMoneyIcon size="sm" />
      </span>
    </button>
  );

  if (option.disabledReason === null) return buyButton;

  const disabledLabel = BUILD_OPTION_DISABLED_REASON_LABEL[option.disabledReason];

  return (
    <Infotip>
      <InfotipTrigger aria-label={disabledLabel} render={<span className="flex min-w-0 flex-1" />}>
        {buyButton}
      </InfotipTrigger>
      <InfotipContent side="top">{disabledLabel}</InfotipContent>
    </Infotip>
  );
}

function BuildOptionRow({
  option,
  owner,
  onChoose,
  onInspect,
  hotkey,
}: {
  option: BuildOption;
  owner: PlayerId;
  onChoose: (kind: UnitKind) => void;
  onInspect: (kind: UnitKind) => void;
  hotkey: string | null;
}) {
  const name = unitDef(option.kind).name;
  const img = getSpriteUnitIconImage(option.kind, owner);

  return (
    <div className="flex w-full min-w-0 items-center gap-2 rounded-lg border bg-card px-2.5 py-1.5 transition hover-supported:bg-muted active:bg-muted">
      <BuildOptionBuyAction
        option={option}
        unitName={name}
        onChoose={onChoose}
        hotkey={hotkey}
        imgSrc={img.src}
      />
      <BuildOptionInfoButton unitName={name} onInspect={() => onInspect(option.kind)} />
    </div>
  );
}

export function BuildOptionsList({
  options,
  owner,
  onChoose,
  onInspect,
}: {
  options: BuildOption[];
  owner: PlayerId;
  onChoose: (kind: UnitKind) => void;
  onInspect: (kind: UnitKind) => void;
}) {
  return (
    <BuildListScrollContainer rowCount={options.length}>
      {options.map((option) => (
        <BuildOptionRow
          key={option.kind}
          option={option}
          owner={owner}
          onChoose={onChoose}
          onInspect={onInspect}
          hotkey={unitBuildHotkey(option.kind)}
        />
      ))}
    </BuildListScrollContainer>
  );
}
